import { ApolloQueryResult, useMutation } from '@apollo/client';
import clsx from 'clsx';
import React, { useState, useEffect } from 'react';

import {
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close, Save } from '@material-ui/icons';

import { Toast } from '../../../../components/Toast';
import { UPDATE_TAG } from '../../../../queries/tag';
import { ITag } from '../../../../typings/tag';
import { getFriendlyErrorMessage } from '../../../../utils/errorHelper';
import useStyles from '../../styles';

interface IEditTagModal {
  isOpened: boolean
  tag?: ITag
  handleClose: () => void
  refetchList: (
    variables?: {
      listTags: ITag[]
    } | undefined
  ) => Promise<ApolloQueryResult<any>>
}

const EditTagModal: React.FC<IEditTagModal> = ({
  isOpened,
  tag,
  handleClose,
  refetchList,
}) => {
  const [TagId, setTagId] = useState<string | undefined>();
  const [tagName, setTagName] = useState<string | undefined>();
  const [permanentTag, setPermanentTag] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});
  const classes = useStyles();

  const [updateTag] = useMutation(UPDATE_TAG);

  useEffect(() => {
    if (tag) {
      setTagId(tag.id);
      setTagName(tag.name);
      setPermanentTag(tag.permanent);
      setErrors({});
    }
  }, [tag]);

  const handleSave = async () => {
    try {
      if (hasValidationError()) return;

      if (tag && (tag.name !== tagName || tag.permanent !== permanentTag)) {
        await updateTag({
          variables: {
            id: TagId,
            name: tagName,
            permanent: permanentTag,
          },
        });

        Toast('Tag updated!');
      }

      handleClose();
      refetchList();
    } catch (error) {
      Toast(getFriendlyErrorMessage(error), 'error');
    }
  };

  const hasValidationError = () => {
    const errors = {
      ...(tagName?.trim() === '' && { tagName: true }),
    };

    setErrors(errors);

    if (Object.values(errors).length > 0) {
      return true;
    }

    return false;
  };

  return (
    <Modal open={isOpened} onClose={handleClose} className={classes.modal}>
      <Fade in={isOpened}>
        <Box className={classes.modalPaper}>
          <Box className={classes.modalHeader}>
            <Box className={classes.modalHeaderSide} />
            <Box className={classes.modalHeaderCenter}>
              <Typography align="center" variant="h4">
                Editing Tag
              </Typography>
            </Box>
            <Box
              className={clsx(
                classes.modalHeaderSide,
                classes.modalCloseContainer,
              )}
            >
              <Box className={classes.modalClose}>
                <IconButton onClick={handleClose}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box className={classes.modalInputs}>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              fullWidth
              value={tagName}
              error={errors.tagName}
              onChange={(e) => setTagName(e.target.value)}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            className={classes.saveButton}
            onClick={handleSave}
            startIcon={<Save />}
          >
            Save
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
};

export default EditTagModal;
